import { supabase } from "@/integrations/supabase/supabaseClient";
import { encryptSensitiveData, safeDecrypt } from "@/lib/security";

export interface DatabaseUserSettings {
  id: string;
  user_id: string;
  theme: string;
  currency: string;
  created_at: string;
  updated_at: string;
  preferred_rate_source?: string | null;
  show_original_currency?: boolean | null;
  fx_rate_staleness_threshold?: unknown | null;
  ynab_personal_access_token?: string | null;
}

export interface UserSettingsRepository {
  get(): Promise<DatabaseUserSettings | null>;
  update(
    updates: Partial<
      Pick<
        DatabaseUserSettings,
        | "theme"
        | "currency"
        | "preferred_rate_source"
        | "show_original_currency"
        | "fx_rate_staleness_threshold"
        | "ynab_personal_access_token"
      >
    >,
  ): Promise<DatabaseUserSettings>;
}

export class SupabaseUserSettingsRepository implements UserSettingsRepository {
  async get(): Promise<DatabaseUserSettings | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    const { data, error } = await supabase
      .from("user_settings")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) throw error;

    if (data && data.ynab_personal_access_token) {
      // Decrypt the token transparently when retrieving
      const decryptedToken = await safeDecrypt(data.ynab_personal_access_token);

      // If decryption failed (returns null), clear the corrupted token
      if (decryptedToken === null) {
        console.warn(
          "[UserSettingsRepository] YNAB token could not be decrypted, clearing corrupted token",
        );
        // Clear the corrupted token from the database
        await this.update({ ynab_personal_access_token: null });
        data.ynab_personal_access_token = null;
      } else {
        data.ynab_personal_access_token = decryptedToken;
      }
    }

    return data;
  }

  async update(
    updates: Partial<
      Pick<
        DatabaseUserSettings,
        | "theme"
        | "currency"
        | "preferred_rate_source"
        | "show_original_currency"
        | "fx_rate_staleness_threshold"
        | "ynab_personal_access_token"
      >
    >,
  ): Promise<DatabaseUserSettings> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    // Encrypt the YNAB token before storing if it's being updated
    const processedUpdates = { ...updates };
    console.debug(
      "[UserSettingsRepository] update called with updates:",
      Object.keys(updates),
    );
    if (processedUpdates.ynab_personal_access_token !== undefined) {
      if (processedUpdates.ynab_personal_access_token === null) {
        console.debug(
          "[UserSettingsRepository] Clearing YNAB token (setting to null)",
        );
        // Leave as null to clear the token
      } else if (processedUpdates.ynab_personal_access_token) {
        console.debug(
          "[UserSettingsRepository] Encrypting YNAB token before storage",
        );
        processedUpdates.ynab_personal_access_token =
          await encryptSensitiveData(
            processedUpdates.ynab_personal_access_token,
          );
        console.debug("[UserSettingsRepository] Token encrypted successfully");
      }
    }

    // Ensure the row exists for this user; create or update based on user_id
    const payload = { user_id: user.id, ...processedUpdates };
    console.debug(
      "[UserSettingsRepository] Executing upsert with payload keys:",
      Object.keys(payload),
    );

    const { data, error } = await supabase
      .from("user_settings")
      .upsert(payload as any, { onConflict: "user_id" })
      .select()
      .single();

    if (error) {
      console.error("[UserSettingsRepository] Database error:", error);
      throw error;
    }
    console.debug(
      "[UserSettingsRepository] Database operation successful, data keys:",
      data ? Object.keys(data) : "null",
    );

    // Decrypt the token in the returned data for consistency
    const result = data as DatabaseUserSettings;
    if (result.ynab_personal_access_token) {
      result.ynab_personal_access_token = await safeDecrypt(
        result.ynab_personal_access_token,
      );
    }

    return result;
  }
}
